import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Package, Plus, RefreshCcw, Search, Download, Trash2, ExternalLink, 
  Globe, CheckCircle2, XCircle, Loader2, TrendingUp, ShoppingBag,
  Clock, AlertTriangle, ArrowRight, Eye, ChevronDown, ChevronUp, X,
  Pencil, XOctagon, DollarSign
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const AdminSuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSupplier, setActiveSupplier] = useState(null);
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [scraping, setScraping] = useState({});
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ name: "", url: "", description: "" });
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [maxPages, setMaxPages] = useState(3);
  const [marginPercent, setMarginPercent] = useState(30);
  const [searchQuery, setSearchQuery] = useState("");
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showLogsPanel, setShowLogsPanel] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", description: "", pricing_tiers: [] });

  const fetchData = useCallback(async () => {
    try {
      const [suppRes, statsRes, logsRes] = await Promise.all([
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/suppliers/stats`),
        axios.get(`${API}/suppliers/logs`),
      ]);
      setSuppliers(suppRes.data);
      setStats(statsRes.data);
      setLogs(logsRes.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addSupplier = async () => {
    if (!newSupplier.name || !newSupplier.url) {
      toast.error("Nom et URL requis");
      return;
    }
    try {
      await axios.post(`${API}/suppliers`, newSupplier);
      toast.success(`${newSupplier.name} ajouté !`);
      setShowAddDialog(false);
      setNewSupplier({ name: "", url: "", description: "" });
      fetchData();
    } catch (e) {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const deleteSupplier = async (id, name) => {
    if (!window.confirm(`Supprimer ${name} et tous ses produits scrapés ?`)) return;
    try {
      await axios.delete(`${API}/suppliers/${id}`);
      toast.success("Fournisseur supprimé");
      if (activeSupplier?.id === id) {
        setActiveSupplier(null);
        setSupplierProducts([]);
      }
      fetchData();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const scrapeSupplier = async (supplier, url = null) => {
    setScraping(prev => ({ ...prev, [supplier.id]: true }));
    try {
      const res = await axios.post(`${API}/suppliers/scrape`, {
        supplier_id: supplier.id,
        url: url || supplier.url,
        max_pages: maxPages,
      });
      toast.success(res.data.message);
      fetchData();
      if (activeSupplier?.id === supplier.id) {
        loadSupplierProducts(supplier.id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du scraping");
    }
    setScraping(prev => ({ ...prev, [supplier.id]: false }));
  };

  const loadSupplierProducts = async (supplierId) => {
    setProductsLoading(true);
    setSelectedProducts([]);
    try {
      const res = await axios.get(`${API}/suppliers/products/${supplierId}`);
      setSupplierProducts(res.data);
    } catch (e) {
      toast.error("Erreur chargement produits");
    }
    setProductsLoading(false);
  };

  const selectSupplier = (supplier) => {
    setActiveSupplier(supplier);
    setSearchQuery("");
    loadSupplierProducts(supplier.id);
  };

  const toggleProduct = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]
    );
  };

  const selectAll = () => {
    const filtered = filteredProducts.filter(p => !p.imported);
    if (selectedProducts.length === filtered.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filtered.map(p => p.id));
    }
  };

  const importSelected = async () => {
    if (selectedProducts.length === 0) {
      toast.error("Sélectionnez des produits");
      return;
    }
    try {
      const res = await axios.post(`${API}/suppliers/import`, {
        product_ids: selectedProducts,
        margin_percent: marginPercent,
      });
      toast.success(res.data.message);
      setSelectedProducts([]);
      setShowImportDialog(false);
      loadSupplierProducts(activeSupplier.id);
      fetchData();
    } catch (e) {
      toast.error("Erreur lors de l'import");
    }
  };

  const deleteProduct = async (productId) => {
    try {
      await axios.delete(`${API}/suppliers/products/${productId}`);
      setSupplierProducts(prev => prev.filter(p => p.id !== productId));
      toast.success("Produit supprimé");
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const removeFromStore = async (product) => {
    try {
      await axios.post(`${API}/suppliers/remove-from-store`, {
        supplier_product_id: product.id,
      });
      toast.success("Produit retiré de la boutique");
      loadSupplierProducts(activeSupplier.id);
      fetchData();
    } catch (e) {
      toast.error("Erreur lors du retrait");
    }
  };

  const openEdit = (product) => {
    setEditProduct(product);
    setEditForm({
      name: product.name || "",
      description: product.description || "",
      pricing_tiers: product.pricing_tiers || [
        { min_qty: 1, max_qty: 49, price: product.price || 0 },
        { min_qty: 50, max_qty: 99, price: Math.round((product.price || 0) * 0.9 * 100) / 100 },
        { min_qty: 100, max_qty: 999, price: Math.round((product.price || 0) * 0.8 * 100) / 100 },
      ],
    });
  };

  const saveEdit = async () => {
    if (!editProduct) return;
    try {
      await axios.put(`${API}/suppliers/products/${editProduct.id}`, {
        name: editForm.name,
        description: editForm.description,
        pricing_tiers: editForm.pricing_tiers,
      });
      toast.success("Produit modifié");
      setEditProduct(null);
      loadSupplierProducts(activeSupplier.id);
    } catch (e) {
      toast.error("Erreur lors de la modification");
    }
  };

  const addTier = () => {
    const tiers = editForm.pricing_tiers;
    const lastMax = tiers.length > 0 ? tiers[tiers.length - 1].max_qty + 1 : 1;
    setEditForm(prev => ({
      ...prev,
      pricing_tiers: [...prev.pricing_tiers, { min_qty: lastMax, max_qty: lastMax + 99, price: 0 }],
    }));
  };

  const removeTier = (index) => {
    setEditForm(prev => ({
      ...prev,
      pricing_tiers: prev.pricing_tiers.filter((_, i) => i !== index),
    }));
  };

  const updateTier = (index, field, value) => {
    setEditForm(prev => ({
      ...prev,
      pricing_tiers: prev.pricing_tiers.map((t, i) =>
        i === index ? { ...t, [field]: field === "price" ? parseFloat(value) || 0 : parseInt(value) || 0 } : t
      ),
    }));
  };

  const filteredProducts = supplierProducts.filter(p =>
    !searchQuery || p.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (iso) => {
    if (!iso) return "Jamais";
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#8C9D78]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-suppliers-page">
      {/* Header */}
      <div className="bg-[#1D3557] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "Fraunces, serif" }}>
                Gestion Fournisseurs
              </h1>
              <p className="text-white/60 mt-1">Scrapez, sélectionnez et importez des produits</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowLogsPanel(!showLogsPanel)}
                variant="outline"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20 rounded-full"
                data-testid="toggle-logs-btn"
              >
                <Clock className="w-4 h-4 mr-2" />
                Historique
              </Button>
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-[#8C9D78] hover:bg-[#7A8B69] rounded-full" data-testid="add-supplier-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un fournisseur
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border border-stone-200 shadow-2xl rounded-2xl max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-xl text-[#1D3557]">Ajouter un fournisseur</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Nom du fournisseur</Label>
                      <Input
                        value={newSupplier.name}
                        onChange={e => setNewSupplier(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Ex: Toptex, Bic Graphic..."
                        data-testid="supplier-name-input"
                      />
                    </div>
                    <div>
                      <Label>URL du site / catalogue</Label>
                      <Input
                        value={newSupplier.url}
                        onChange={e => setNewSupplier(prev => ({ ...prev, url: e.target.value }))}
                        placeholder="https://www.fournisseur.com/catalogue"
                        data-testid="supplier-url-input"
                      />
                    </div>
                    <div>
                      <Label>Description (optionnel)</Label>
                      <Input
                        value={newSupplier.description}
                        onChange={e => setNewSupplier(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Textile, goodies..."
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={addSupplier} className="bg-[#8C9D78] hover:bg-[#7A8B69] rounded-full" data-testid="confirm-add-supplier">
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stats-cards">
            {[
              { label: "Fournisseurs", value: stats.total_suppliers, icon: Globe, color: "#1D3557" },
              { label: "Produits scrapés", value: stats.total_scraped, icon: Package, color: "#8C9D78" },
              { label: "Importés en boutique", value: stats.total_imported, icon: Download, color: "#C65D47" },
              { label: "Produits en boutique", value: stats.total_store_products, icon: ShoppingBag, color: "#FFD700" },
            ].map((stat, i) => (
              <Card key={i} className="border-0 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${stat.color}15` }}>
                      <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#1D3557]">{stat.value}</p>
                      <p className="text-xs text-stone-500">{stat.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Scrape Logs Panel */}
        {showLogsPanel && logs.length > 0 && (
          <div className="mb-8 bg-white rounded-2xl shadow-sm p-6 border border-stone-200" data-testid="logs-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#1D3557] flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Historique des scrapes
              </h3>
              <button onClick={() => setShowLogsPanel(false)}><X className="w-5 h-5 text-stone-400" /></button>
            </div>
            <div className="space-y-2">
              {logs.map((log, i) => (
                <div key={i} className="flex items-center gap-4 py-2 border-b border-stone-100 last:border-0">
                  {log.status === "success" ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  )}
                  <span className="font-medium text-sm text-[#1D3557]">{log.supplier_name}</span>
                  <span className="text-sm text-stone-500">{log.products_found} produits</span>
                  <span className="text-xs text-stone-400 ml-auto">{formatDate(log.timestamp)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Suppliers List */}
          <div className="lg:col-span-4">
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
              <div className="p-5 border-b border-stone-100">
                <h3 className="font-semibold text-[#1D3557] flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Mes Fournisseurs ({suppliers.length})
                </h3>
              </div>

              {suppliers.length === 0 ? (
                <div className="p-8 text-center">
                  <Globe className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500 text-sm mb-4">Aucun fournisseur ajouté</p>
                  <Button onClick={() => setShowAddDialog(true)} size="sm" className="bg-[#8C9D78] hover:bg-[#7A8B69] rounded-full">
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {suppliers.map(supplier => (
                    <div
                      key={supplier.id}
                      className={`p-4 cursor-pointer transition-all hover:bg-stone-50 ${activeSupplier?.id === supplier.id ? "bg-[#8C9D78]/10 border-l-4 border-l-[#8C9D78]" : ""}`}
                      onClick={() => selectSupplier(supplier)}
                      data-testid={`supplier-${supplier.id}`}
                    >
                      <div className="flex items-start gap-3">
                        {supplier.logo_url ? (
                          <img src={supplier.logo_url} alt="" className="w-8 h-8 rounded-lg bg-stone-100 object-contain flex-shrink-0" />
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-[#1D3557] flex items-center justify-center flex-shrink-0">
                            <Globe className="w-4 h-4 text-white" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-[#1D3557] text-sm truncate">{supplier.name}</p>
                            <Badge variant="outline" className={`text-[10px] px-1.5 ${
                              supplier.status === "scraping" ? "border-yellow-400 text-yellow-600" :
                              supplier.status === "error" ? "border-red-400 text-red-600" :
                              "border-green-400 text-green-600"
                            }`}>
                              {supplier.status === "scraping" ? "Scraping..." : supplier.status === "error" ? "Erreur" : "Actif"}
                            </Badge>
                          </div>
                          <p className="text-xs text-stone-400 truncate mt-0.5">{supplier.url}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-stone-500">
                            <span>{supplier.product_count || 0} scrapés</span>
                            <span className="text-[#8C9D78]">{supplier.imported_count || 0} importés</span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteSupplier(supplier.id, supplier.name); }}
                          className="text-stone-300 hover:text-red-500 transition-colors"
                          data-testid={`delete-supplier-${supplier.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Product Catalog */}
          <div className="lg:col-span-8">
            {!activeSupplier ? (
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-12 text-center">
                <Package className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-[#1D3557] mb-2">Sélectionnez un fournisseur</h3>
                <p className="text-stone-500 mb-6">Choisissez un fournisseur à gauche pour voir et importer ses produits</p>
                <Button onClick={() => setShowAddDialog(true)} className="bg-[#8C9D78] hover:bg-[#7A8B69] rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Ou ajoutez un nouveau fournisseur
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Supplier header + scrape controls */}
                <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-[#1D3557]">{activeSupplier.name}</h3>
                      <p className="text-sm text-stone-400">
                        {activeSupplier.last_sync ? `Dernier scrape: ${formatDate(activeSupplier.last_sync)}` : "Pas encore scrapé"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2 bg-stone-50 rounded-full px-3 py-1">
                        <Label className="text-xs text-stone-500 whitespace-nowrap">Pages max:</Label>
                        <Input
                          type="number"
                          value={maxPages}
                          onChange={e => setMaxPages(parseInt(e.target.value) || 1)}
                          min={1}
                          max={10}
                          className="w-14 h-7 text-xs border-0 bg-transparent p-1 text-center"
                        />
                      </div>
                      <Button
                        onClick={() => scrapeSupplier(activeSupplier)}
                        disabled={scraping[activeSupplier.id]}
                        className="bg-[#C65D47] hover:bg-[#A84A38] rounded-full"
                        data-testid="scrape-btn"
                      >
                        {scraping[activeSupplier.id] ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scraping...</>
                        ) : (
                          <><RefreshCcw className="w-4 h-4 mr-2" />Scraper les produits</>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Custom URL scrape */}
                  <div className="mt-4 pt-4 border-t border-stone-100">
                    <Label className="text-xs text-stone-500 mb-2 block">Scraper une URL spécifique de ce fournisseur :</Label>
                    <div className="flex gap-2">
                      <Input
                        value={scrapeUrl}
                        onChange={e => setScrapeUrl(e.target.value)}
                        placeholder={`https://${new URL(activeSupplier.url).hostname}/categorie/...`}
                        className="flex-1 rounded-full h-9 text-sm"
                        data-testid="scrape-url-input"
                      />
                      <Button
                        onClick={() => { if (scrapeUrl) scrapeSupplier(activeSupplier, scrapeUrl); }}
                        disabled={!scrapeUrl || scraping[activeSupplier.id]}
                        size="sm"
                        className="bg-[#1D3557] hover:bg-[#152A45] rounded-full"
                        data-testid="scrape-url-btn"
                      >
                        <Globe className="w-4 h-4 mr-1" />
                        Scraper cette URL
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Products toolbar */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                      <Input
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Rechercher..."
                        className="pl-9 h-9 w-56 rounded-full text-sm"
                        data-testid="product-search-input"
                      />
                    </div>
                    <p className="text-sm text-stone-500">
                      {filteredProducts.length} produit{filteredProducts.length > 1 ? "s" : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={selectAll} variant="outline" size="sm" className="rounded-full text-xs" data-testid="select-all-btn">
                      {selectedProducts.length === filteredProducts.filter(p => !p.imported).length && selectedProducts.length > 0 ? "Tout désélectionner" : "Tout sélectionner"}
                    </Button>
                    {selectedProducts.length > 0 && (
                      <Button
                        onClick={() => setShowImportDialog(true)}
                        size="sm"
                        className="bg-[#8C9D78] hover:bg-[#7A8B69] rounded-full"
                        data-testid="import-btn"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Importer {selectedProducts.length} produit{selectedProducts.length > 1 ? "s" : ""}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Products grid */}
                {productsLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-8 h-8 animate-spin text-[#8C9D78]" />
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-12 text-center">
                    <Package className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                    <p className="text-stone-500 mb-4">Aucun produit scrapé pour ce fournisseur</p>
                    <Button
                      onClick={() => scrapeSupplier(activeSupplier)}
                      disabled={scraping[activeSupplier.id]}
                      className="bg-[#C65D47] hover:bg-[#A84A38] rounded-full"
                    >
                      <RefreshCcw className="w-4 h-4 mr-2" />
                      Lancer un scraping
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="supplier-products-grid">
                    {filteredProducts.map(product => (
                      <div
                        key={product.id}
                        className={`bg-white rounded-xl border overflow-hidden transition-all ${
                          product.imported
                            ? "border-green-200 bg-green-50/30"
                            : selectedProducts.includes(product.id)
                            ? "border-[#8C9D78] ring-2 ring-[#8C9D78]/20 shadow-md"
                            : "border-stone-200 hover:shadow-md"
                        }`}
                        data-testid={`supplier-product-${product.id}`}
                      >
                        {/* Image */}
                        <div className="relative aspect-square bg-stone-100 overflow-hidden">
                          {product.image_url ? (
                            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-10 h-10 text-stone-300" />
                            </div>
                          )}

                          {/* Checkbox / Imported badge */}
                          {product.imported ? (
                            <div className="absolute top-2 left-2">
                              <Badge className="bg-green-500 text-white text-[10px] px-2">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Importé
                              </Badge>
                            </div>
                          ) : (
                            <button
                              onClick={() => toggleProduct(product.id)}
                              className={`absolute top-2 left-2 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                                selectedProducts.includes(product.id)
                                  ? "bg-[#8C9D78] border-[#8C9D78]"
                                  : "bg-white/80 border-stone-300 hover:border-[#8C9D78]"
                              }`}
                            >
                              {selectedProducts.includes(product.id) && (
                                <CheckCircle2 className="w-4 h-4 text-white" />
                              )}
                            </button>
                          )}

                          {/* Actions */}
                          <div className="absolute top-2 right-2 flex gap-1">
                            <button
                              onClick={() => openEdit(product)}
                              className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center hover:bg-blue-50 shadow-sm"
                              title="Modifier"
                            >
                              <Pencil className="w-3.5 h-3.5 text-stone-600 hover:text-blue-500" />
                            </button>
                            {product.source_url && (
                              <a
                                href={product.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center hover:bg-white shadow-sm"
                                onClick={e => e.stopPropagation()}
                              >
                                <ExternalLink className="w-3.5 h-3.5 text-stone-600" />
                              </a>
                            )}
                            {product.imported ? (
                              <button
                                onClick={() => removeFromStore(product)}
                                className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center hover:bg-red-50 shadow-sm"
                                title="Retirer de la boutique"
                              >
                                <XOctagon className="w-3.5 h-3.5 text-red-400 hover:text-red-600" />
                              </button>
                            ) : (
                              <button
                                onClick={() => deleteProduct(product.id)}
                                className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center hover:bg-red-50 shadow-sm"
                                title="Supprimer"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-stone-400 hover:text-red-500" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Info */}
                        <div className="p-3">
                          <p className="font-medium text-[#1D3557] text-sm line-clamp-2 min-h-[2.5rem]">{product.name}</p>
                          {product.price_text && (
                            <p className="text-[#C65D47] font-bold text-sm mt-1">{product.price_text}</p>
                          )}
                          {product.category && (
                            <p className="text-xs text-stone-400 mt-1">{product.category}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="bg-white border border-stone-200 shadow-2xl rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#1D3557]">Importer dans la boutique</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <p className="text-stone-600">
              Vous allez importer <strong className="text-[#1D3557]">{selectedProducts.length}</strong> produit{selectedProducts.length > 1 ? "s" : ""} dans votre boutique.
            </p>
            <div>
              <Label className="text-sm font-medium text-[#1D3557]">Marge à appliquer (%)</Label>
              <div className="flex items-center gap-3 mt-2">
                <Input
                  type="number"
                  value={marginPercent}
                  onChange={e => setMarginPercent(parseFloat(e.target.value) || 0)}
                  min={0}
                  max={500}
                  className="w-24 bg-white border-stone-300 focus:border-[#8C9D78] text-[#1D3557] font-semibold"
                  data-testid="margin-input"
                />
                <span className="text-sm text-stone-500">% ajouté au prix fournisseur</span>
              </div>
            </div>
            <div className="p-4 bg-[#8C9D78]/10 rounded-xl border border-[#8C9D78]/20">
              <p className="text-sm text-[#1D3557]">
                <strong>Exemple :</strong> Un produit à 10€ fournisseur sera vendu à{" "}
                <strong className="text-[#C65D47]">{(10 * (1 + marginPercent / 100)).toFixed(2)}€</strong> dans votre boutique.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowImportDialog(false)} className="rounded-full border-stone-300">Annuler</Button>
            <Button onClick={importSelected} className="bg-[#8C9D78] hover:bg-[#7A8B69] rounded-full" data-testid="confirm-import-btn">
              <Download className="w-4 h-4 mr-2" />
              Importer {selectedProducts.length} produit{selectedProducts.length > 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={!!editProduct} onOpenChange={(open) => { if (!open) setEditProduct(null); }}>
        <DialogContent className="bg-white border border-stone-200 shadow-2xl rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#1D3557]">Modifier le produit</DialogTitle>
          </DialogHeader>
          {editProduct && (
            <div className="space-y-5 py-2 max-h-[70vh] overflow-y-auto">
              {/* Product image preview */}
              {editProduct.image_url && (
                <div className="flex justify-center">
                  <img src={editProduct.image_url} alt="" className="w-24 h-24 object-cover rounded-xl border border-stone-200" />
                </div>
              )}

              {/* Name */}
              <div>
                <Label className="text-sm font-medium text-[#1D3557]">Nom du produit</Label>
                <Input
                  value={editForm.name}
                  onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1 bg-white border-stone-300"
                  data-testid="edit-product-name"
                />
              </div>

              {/* Description */}
              <div>
                <Label className="text-sm font-medium text-[#1D3557]">Description</Label>
                <textarea
                  value={editForm.description}
                  onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 text-sm bg-white focus:border-[#8C9D78] focus:outline-none focus:ring-1 focus:ring-[#8C9D78]"
                  data-testid="edit-product-desc"
                />
              </div>

              {/* Pricing Tiers */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-medium text-[#1D3557] flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-[#8C9D78]" />
                    Prix par quantité
                  </Label>
                  <Button onClick={addTier} variant="outline" size="sm" className="rounded-full text-xs h-7 px-3" data-testid="add-tier-btn">
                    <Plus className="w-3 h-3 mr-1" />
                    Ajouter un palier
                  </Button>
                </div>

                {editForm.pricing_tiers.length === 0 ? (
                  <p className="text-sm text-stone-400 text-center py-3 bg-stone-50 rounded-lg">
                    Aucun palier de prix. Le prix de base sera utilisé.
                  </p>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 text-xs font-medium text-stone-500 px-1">
                      <div className="col-span-4">De (qté)</div>
                      <div className="col-span-4">À (qté)</div>
                      <div className="col-span-3">Prix HT (€)</div>
                      <div className="col-span-1"></div>
                    </div>
                    {editForm.pricing_tiers.map((tier, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center">
                        <Input
                          type="number"
                          value={tier.min_qty}
                          onChange={e => updateTier(i, "min_qty", e.target.value)}
                          min={1}
                          className="col-span-4 h-9 text-sm bg-white border-stone-300"
                          data-testid={`tier-min-${i}`}
                        />
                        <Input
                          type="number"
                          value={tier.max_qty}
                          onChange={e => updateTier(i, "max_qty", e.target.value)}
                          min={1}
                          className="col-span-4 h-9 text-sm bg-white border-stone-300"
                          data-testid={`tier-max-${i}`}
                        />
                        <Input
                          type="number"
                          value={tier.price}
                          onChange={e => updateTier(i, "price", e.target.value)}
                          min={0}
                          step={0.01}
                          className="col-span-3 h-9 text-sm bg-white border-stone-300 font-semibold text-[#1D3557]"
                          data-testid={`tier-price-${i}`}
                        />
                        <button
                          onClick={() => removeTier(i)}
                          className="col-span-1 flex items-center justify-center text-stone-300 hover:text-red-500 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {editForm.pricing_tiers.length > 0 && (
                  <div className="mt-3 p-3 bg-[#1D3557]/5 rounded-lg">
                    <p className="text-xs text-stone-500 font-medium mb-1">Aperçu des prix :</p>
                    <div className="flex flex-wrap gap-2">
                      {editForm.pricing_tiers.map((tier, i) => (
                        <span key={i} className="text-xs bg-white px-2 py-1 rounded-full border border-stone-200">
                          {tier.min_qty}-{tier.max_qty} unités : <strong className="text-[#C65D47]">{tier.price.toFixed(2)}€</strong>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditProduct(null)} className="rounded-full border-stone-300">Annuler</Button>
            <Button onClick={saveEdit} className="bg-[#8C9D78] hover:bg-[#7A8B69] rounded-full" data-testid="save-edit-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSuppliersPage;
